import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tenderAPI } from '../utils/api';
import { Search, Bell, ChevronDown, LogOut, User, Check, Radar, FileText, X, Trash2, Menu } from 'lucide-react';

const Topbar = ({ onMobileToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await tenderAPI.notificationUnreadCount();
      setUnreadCount(res.data.unread || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const fetchNotifications = async () => {
    setLoadingNotifs(true);
    try {
      const res = await tenderAPI.notificationItems({ limit: 15 });
      setNotifications(res.data.items || []);
      setUnreadCount(res.data.unread || 0);
    } catch (e) { console.error(e); }
    finally { setLoadingNotifs(false); }
  };

  const handleBellClick = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
    setShowDropdown(false);
  };

  const handleMarkRead = async (id) => {
    try {
      await tenderAPI.notificationMarkRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error(e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await tenderAPI.notificationMarkAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { console.error(e); }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) handleMarkRead(notif._id);
    if (notif.tender_id) {
      navigate(`/tender-scout/tenders/${notif.tender_id}`);
      setShowNotifications(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await tenderAPI.notificationDelete(id);
      setNotifications(prev => prev.filter(n => n._id !== id));
    } catch (e) { console.error(e); }
  };

  const getNotifIcon = (type) => {
    if (type === 'high_score_tender') return <Radar size={14} className="text-green-600" />;
    if (type === 'scrape_complete') return <FileText size={14} className="text-blue-600" />;
    return <Bell size={14} className="text-slate-500" />;
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <header className="bg-white border-b border-slate-200 px-3 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
      {/* Mobile hamburger */}
      <button
        onClick={onMobileToggle}
        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors lg:hidden flex-shrink-0"
        data-testid="mobile-menu-btn"
      >
        <Menu size={20} />
      </button>

      {/* Search */}
      <div className="flex-1 max-w-md hidden sm:block">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            data-testid="global-search-input"
          />
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-4">
        {/* Notifications Bell */}
        <div className="relative">
          <button
            onClick={handleBellClick}
            className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="notification-bell-btn"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1" data-testid="notification-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
              <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-[384px] bg-white rounded-lg shadow-xl border border-slate-200 z-20 overflow-hidden" data-testid="notification-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary hover:underline"
                        data-testid="mark-all-read-btn"
                      >
                        Mark all read
                      </button>
                    )}
                    <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-600">
                      <X size={16} />
                    </button>
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {loadingNotifs ? (
                    <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
                  ) : notifications.length === 0 ? (
                    <div className="py-8 text-center">
                      <Bell size={28} className="text-slate-200 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No notifications yet</p>
                      <p className="text-xs text-slate-300 mt-1">Notifications appear when high-score tenders are found</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n._id}
                        onClick={() => handleNotifClick(n)}
                        className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50 ${
                          !n.read ? 'bg-primary/[0.03]' : ''
                        }`}
                        data-testid={`notification-item-${n._id}`}
                      >
                        <div className="mt-0.5 flex-shrink-0">{getNotifIcon(n.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm leading-tight ${!n.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                              {n.title}
                            </p>
                            {!n.read && <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-slate-400">{timeAgo(n.created_date)}</span>
                            {n.source_name && <span className="text-[10px] text-slate-400">| {n.source_name}</span>}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDelete(e, n._id)}
                          className="text-slate-300 hover:text-red-400 flex-shrink-0 mt-0.5"
                          data-testid={`delete-notif-${n._id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-center">
                    <button
                      onClick={() => { navigate('/tender-scout/tenders'); setShowNotifications(false); }}
                      className="text-xs text-primary hover:underline"
                      data-testid="view-all-tenders-link"
                    >
                      View All Tenders
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => { setShowDropdown(!showDropdown); setShowNotifications(false); }}
            className="flex items-center gap-2 p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="user-menu-btn"
          >
            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <ChevronDown size={16} className="text-slate-400" />
          </button>

          {showDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowDropdown(false)}
              />
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-20">
                <div className="px-4 py-2 border-b border-slate-200">
                  <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-500">{user?.role}</p>
                </div>
                <button
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="profile-menu-btn"
                >
                  <User size={16} />
                  Profile
                </button>
                <button
                  onClick={() => {
                    logout();
                    setShowDropdown(false);
                    window.location.href = '/login';
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
